import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { ISTRES_LOGO } from "@/lib/istres";
import { formatApiError } from "@/lib/api";
import { ArrowRight, ArrowLeft, Sparkles, Lock, UserPlus } from "lucide-react";

export default function LoginPage() {
  const { login, register, demoLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = location.pathname === "/register" ? "register" : "login";
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [regEmail, setRegEmail] = useState("");
  const [regPass, setRegPass] = useState("");
  const [regName, setRegName] = useState("");
  const [regPseudo, setRegPseudo] = useState("");
  const [regRole, setRegRole] = useState("habitant");
  const [merchantName, setMerchantName] = useState("");
  const [merchantCategory, setMerchantCategory] = useState("");

  const onLogin = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Salut ${u.pseudo || u.name} 👋`);
      navigate("/");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const onRegister = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const u = await register({
        email: regEmail, password: regPass, name: regName, pseudo: regPseudo,
        role: regRole,
        merchant_name: regRole === "commercant" ? merchantName : undefined,
        merchant_category: regRole === "commercant" ? merchantCategory : undefined,
      });
      toast.success(`Bienvenue ${u.name} ! Pseudo en attente de validation.`);
      navigate("/");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const onDemo = async (role) => {
    setBusy(true);
    try {
      const u = await demoLogin(role);
      toast.success(`Démo · ${u.pseudo || u.name} 🚀`);
      navigate("/");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const demoButtons = [
    { role: "habitant",  label: "Habitant",   emoji: "🙋", color: "bg-pink-50 border-pink-200 hover:bg-pink-100", text: "text-pink-600", id: "login-habitant-btn" },
    { role: "commercant",label: "Commerçant", emoji: "🛍️", color: "bg-sun-50 border-sun-200 hover:bg-sun-100", text: "text-sun-700", id: "login-commercant-btn" },
    { role: "admin",     label: "Mairie",     emoji: "🏛️", color: "bg-plum-50 border-plum-200 hover:bg-plum-100", text: "text-plum-700", id: "login-admin-btn" },
  ];

  return (
    <div className="min-h-screen bg-cream-50 grid lg:grid-cols-[1fr_minmax(420px,520px)]">
      {/* ============ Left: city panorama + welcome message ============ */}
      <aside className="hidden lg:flex relative overflow-hidden bg-gradient-to-br from-plum-700 via-plum-800 to-plum-900 text-cream-50 flex-col justify-between p-12 xl:p-16">
        {/* Decorative gradients */}
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-sun-400/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-pink-400/15 blur-3xl pointer-events-none" />

        <Link to="/" className="relative inline-flex items-center gap-2 text-cream-50/75 hover:text-cream-50 text-[13px] font-semibold w-fit" data-testid="login-back-home">
          <ArrowLeft size={14} /> Retour à l&apos;accueil
        </Link>

        <div className="relative space-y-7 max-w-md">
          <Link to="/" className="flex items-center gap-0 group" aria-label="Istres & Moi · Accueil">
            <img src={ISTRES_LOGO} alt="Logo Ville d'Istres" className="h-20 w-auto object-contain drop-shadow-[0_4px_24px_rgba(255,213,0,0.2)] group-hover:scale-105 transition-transform" />
            <div className="leading-none -ml-4">
              <div className="font-display font-extrabold text-[34px] tracking-tight">Istres &amp; Moi</div>
              <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-cream-50/65 mt-1.5">Ville d&apos;Istres</div>
            </div>
          </Link>

          <h1 className="heading-display text-[3rem] xl:text-[3.6rem] leading-[1.02] text-balance">
            Istres,
            <br />
            <span className="text-sun-300">c&apos;est Vous</span>.
          </h1>

          <p className="text-[16px] xl:text-[17px] text-cream-50/85 leading-relaxed">
            Plateforme officielle de la mairie pour partager vos idées, répondre aux sondages, gagner des bons chez vos commerçants.
          </p>

          <ul className="space-y-2.5 text-[14px] text-cream-50/80">
            {[
              "Boîte à idées municipale, accessible 24 h/24",
              "Sondages récompensés par des bons",
              "Annuaire des 812 commerces d'Istres",
              "Marchés, événements, défibrillateurs sur une carte",
            ].map((it) => (
              <li key={it} className="flex items-start gap-2.5">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-sun-300 shrink-0" />
                {it}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative text-[11px] text-cream-50/55">
          © {new Date().getFullYear()} Mairie d&apos;Istres — Plateforme officielle
        </div>
      </aside>

      {/* ============ Right: auth form ============ */}
      <main className="flex flex-col p-5 sm:p-10 lg:p-14">
        <Link to="/" className="lg:hidden inline-flex items-center gap-2 text-[13px] font-semibold text-plum-700/70 hover:text-plum-700 mb-5" data-testid="login-back-home-mobile">
          <ArrowLeft size={14} /> Retour
        </Link>

        {/* Mobile header */}
        <Link to="/" className="lg:hidden flex items-center gap-0 mb-6">
          <img src={ISTRES_LOGO} alt="Logo Ville d'Istres" className="h-12 w-auto object-contain" />
          <div className="leading-none -ml-2">
            <div className="font-display font-bold text-plum-700 text-[20px] tracking-tight">Istres &amp; Moi</div>
            <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-plum-700/55 mt-1">Ville d&apos;Istres</div>
          </div>
        </Link>

        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
          <h2 className="font-display font-extrabold text-[1.75rem] sm:text-[2rem] text-plum-900 leading-tight mb-1">Bienvenue 👋</h2>
          <p className="text-[14px] text-plum-700/65 mb-7">Connectez-vous ou créez votre compte en quelques secondes.</p>

          {/* Demo quick-login */}
          <div className="mb-6">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-plum-700/55 mb-2.5 flex items-center gap-1.5">
              <Sparkles size={11} /> Essai rapide en 1 clic
            </div>
            <div className="grid grid-cols-3 gap-2">
              {demoButtons.map((b) => (
                <button
                  key={b.role}
                  onClick={() => onDemo(b.role)}
                  disabled={busy}
                  data-testid={b.id}
                  className={`rounded-2xl border-2 ${b.color} px-2 py-3 transition-all hover:-translate-y-0.5 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center gap-1.5`}
                >
                  <span className="text-2xl" aria-hidden="true">{b.emoji}</span>
                  <span className={`text-[12px] font-bold ${b.text} leading-none`}>{b.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px bg-cream-200 flex-1" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-plum-700/45">ou avec votre compte</span>
            <div className="h-px bg-cream-200 flex-1" />
          </div>

          <Tabs defaultValue={initialTab} className="w-full" data-testid="login-tabs">
            <TabsList className="grid grid-cols-2 bg-cream-100 rounded-full p-1 h-12">
              <TabsTrigger value="login" data-testid="login-tab-login" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-plum-700 font-semibold text-[13px] flex items-center gap-1.5">
                <Lock size={12} /> Se connecter
              </TabsTrigger>
              <TabsTrigger value="register" data-testid="login-tab-register" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-plum-700 font-semibold text-[13px] flex items-center gap-1.5">
                <UserPlus size={12} /> S&apos;inscrire
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-5">
              <form onSubmit={onLogin} className="space-y-3" data-testid="login-form">
                <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="vous@email.fr" testid="login-email" />
                <Field label="Mot de passe" type="password" value={password} onChange={setPassword} placeholder="6 caractères minimum" testid="login-password" />
                <Button type="submit" disabled={busy} data-testid="login-submit-btn" className="rounded-full bg-plum-700 hover:bg-plum-800 text-white w-full h-12 mt-1.5 font-bold inline-flex items-center justify-center gap-2">
                  Se connecter <ArrowRight size={14} />
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-5">
              <form onSubmit={onRegister} className="space-y-3" data-testid="register-form">
                <div className="grid grid-cols-2 gap-2.5">
                  <Field label="Prénom" value={regName} onChange={setRegName} placeholder="Jeanne" testid="register-name" />
                  <Field label="Pseudo" value={regPseudo} onChange={setRegPseudo} placeholder="jeanne13" testid="register-pseudo" />
                </div>
                <Field label="Email" type="email" value={regEmail} onChange={setRegEmail} placeholder="vous@email.fr" testid="register-email" />
                <Field label="Mot de passe" type="password" value={regPass} onChange={setRegPass} placeholder="6 caractères min." testid="register-password" />

                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold uppercase tracking-wider text-plum-700/70">Vous êtes</Label>
                  <Select value={regRole} onValueChange={setRegRole}>
                    <SelectTrigger className="rounded-xl h-11 bg-white" data-testid="register-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="habitant">🙋 Habitant d&apos;Istres</SelectItem>
                      <SelectItem value="commercant">🛍️ Commerçant local</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {regRole === "commercant" && (
                  <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-cream-200">
                    <Field label="Nom commerce" value={merchantName} onChange={setMerchantName} placeholder="Bijouterie Éclat" testid="register-merchant-name" />
                    <Field label="Catégorie" value={merchantCategory} onChange={setMerchantCategory} placeholder="Bijouterie" testid="register-merchant-cat" />
                  </div>
                )}

                <Button type="submit" disabled={busy} data-testid="register-submit-btn" className="rounded-full bg-pink-500 hover:bg-pink-600 text-white w-full h-12 mt-1.5 font-bold inline-flex items-center justify-center gap-2">
                  Créer mon compte <ArrowRight size={14} />
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="mt-7 text-[11px] text-plum-700/55 text-center leading-relaxed">
            En continuant, vous acceptez nos {" "}
            <Link to="/mentions-legales" className="underline hover:text-plum-700">mentions légales</Link>{" "}et notre{" "}
            <Link to="/confidentialite" className="underline hover:text-plum-700">politique de confidentialité</Link>.
          </p>
        </div>
      </main>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, placeholder, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-semibold uppercase tracking-wider text-plum-700/70">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="rounded-xl h-11 bg-white border-cream-200"
      />
    </div>
  );
}
